import React, { Component } from 'react';
import {
  arrayOf,
  number,
  string,
  func,
  shape,
  bool,
  any,
  oneOf,
} from 'prop-types';
import styled from 'styled-components/macro';
import detectIt from 'detect-it';

import SavedDSPLists from '../SavedDSPLists/SavedDSPLists';
import SearchField from 'components/styled/SearchField/SearchField';
import Checkbox from 'components/base/Checkbox/Checkbox';
import { helperText } from 'styles/base-classes';
import { hideVisually } from 'polished';
import escapeRegExp from 'helpers/escapeRegExp';
import { definitionLabel } from 'styles/base-classes';

const Label = styled('p')`
  ${definitionLabel};
  font-size: 11px;
  margin: ${({ margin }) => (margin ? margin : '20px 0 10px 0')};
`;

const Container = styled('div')`
  ${({ isDropdownDesign, theme }) =>
    isDropdownDesign
      ? 'height: 90%'
      : `
  @media screen and (min-width: 50em) {
    border: 1px solid ${theme.Grey};
  }
`}
`;

const Header = styled('div')`
  ${({ isDropdownDesign, theme }) =>
    isDropdownDesign
      ? `
    margin: 20px 0 10px 0;
      `
      : `
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  @media screen and (min-width: 50em) {
    margin-bottom: 0;
    padding: ${theme.spacing.small};
    border-bottom: 1px solid ${theme.Grey};
  }
`}
`;

const Content = styled('div')`
  ${({ dropdownMenu, theme, smoothScrolling }) =>
    dropdownMenu
      ? `
    padding: ${theme.spacing.small};
    max-height: 26.5em;
    overflow-y: ${smoothScrolling ? 'scroll' : 'auto'};
    overscroll-behavior-y: contain;
    position: relative;
    -webkit-overflow-scrolling: touch;
  `
      : ''}
  @media screen and (min-width: 50em) {
    padding: ${({ theme }) => theme.spacing.small};
    max-height: ${({ dropdownMenu }) => (dropdownMenu ? '26.5em' : '25em')};
    overflow-y: ${({ smoothScrolling }) =>
      smoothScrolling ? 'scroll' : 'auto'};
    overscroll-behavior-y: contain;
    position: relative;
    -webkit-overflow-scrolling: touch;
  }
  ${({ isDropdownDesign }) =>
    isDropdownDesign
      ? `
  max-height: 90%;
  @media screen and (min-width: 50em) {
    max-height: 90%;
  }
  `
      : ''}
`;

const ListItem = styled('li')`
  margin: ${({ theme }) => theme.spacing.small} 0;

  @media screen and (min-width: 50em) {
    margin: ${({ theme }) => theme.spacing.xxsmall} 0;
  }

  input {
    ${hideVisually};
  }
`;

const HelperText = styled('p')`
  ${helperText};
`;

const CheckboxLabel = styled('label')`
  display: flex;
  align-items: center;

  ${({ isDropdownDesign }) =>
    isDropdownDesign
      ? `
    font-size: 16px;
    line-height: 32px;
  `
      : ''}

  > span {
    transition: ${({ theme }) =>
      `opacity ${theme.transitionDuration} ${theme.timingFunction}`};
    opacity: ${({ checked, dspLimitReached, isDropdownDesign }) =>
      dspLimitReached && !checked && !isDropdownDesign ? 0.4 : 1};
    color: ${({ isDropdownDesign, checked, dspLimitReached }) =>
      isDropdownDesign && !checked && dspLimitReached ? '#8DA5BF' : '#000000'};
  }

  svg {
    transition: ${({ theme }) =>
      `opacity ${theme.transitionDuration} ${theme.timingFunction}`};
    color: ${({ theme }) => theme.Grey};
    opacity: ${({ checked, minDSPLimitReached }) =>
      minDSPLimitReached && checked ? 0.4 : 1};
  }
`;

const { hasTouch, hasMouse } = detectIt;
const smoothScrolling = hasTouch && !hasMouse;

/** List displaying all DSPs coming from the relevant API call. */
class DSPList extends Component {
  state = {
    searchValue: '',
  };

  handleSearch = ({ target }) => {
    this.setState({ searchValue: target.value });
  };

  render() {
    const {
      allDSPs,
      selectedDSPs,
      savedCustomDSPLists,
      onSelectCustomDSPList,
      selectedCustomDSPList,
      switchToEditView,
      toggleDSP,
      dspLimitReached,
      isDropdownDesign,
      label,
    } = this.props;
    const input = escapeRegExp(this.state.searchValue);
    const regex = new RegExp(input, 'gi');

    const filteredDspItems = allDSPs?.filter(
      (dsp) => dsp.name.match(regex) || dsp.displayName.match(regex)
    );

    const filteredSavedCustomDSPLists = savedCustomDSPLists?.filter((list) =>
      list?.DSPList.every((dspName) =>
        filteredDspItems.find((dsp) => dsp.name === dspName)
      )
    );
    const filteredCustomDSPItems = filteredSavedCustomDSPLists?.filter((dsp) =>
      dsp.displayName.match(regex)
    );

    let selectedDSPsIds;

    if (selectedDSPs?.[0]?.id) {
      selectedDSPsIds = selectedDSPs.map((dsp) => dsp.id);
    }

    return (
      <Container isDropdownDesign={isDropdownDesign}>
        <Header isDropdownDesign={isDropdownDesign}>
          <SearchField
            handleSearch={this.handleSearch}
            value={this.state.searchValue}
            placeholder="Search"
          />
        </Header>

        <Content
          smoothScrolling={smoothScrolling}
          dropdownMenu={!!filteredCustomDSPItems}
          isDropdownDesign={isDropdownDesign}
        >
          {!filteredCustomDSPItems?.length &&
            label &&
            !!filteredDspItems?.length && (
              <Label margin="0 0 10px 0">{label}</Label>
            )}
          {!!filteredCustomDSPItems?.length && (
            <>
              <SavedDSPLists
                savedCustomDSPLists={filteredCustomDSPItems}
                customers={allDSPs}
                selectedDSPs={selectedDSPs}
                selectedCustomDSPList={selectedCustomDSPList}
                onSelectCustomDSPList={onSelectCustomDSPList}
                switchToEditView={switchToEditView}
              />
              {label && !!filteredDspItems?.length && <Label>{label}</Label>}
            </>
          )}
          {filteredDspItems.length > 0 ? (
            <ul>
              {filteredDspItems.map((item) => {
                const name = item.name;
                const isChecked = (selectedDSPsIds || selectedDSPs).includes(
                  name
                );
                const handleChange = () => toggleDSP(item);

                return (
                  <ListItem key={`dsp-list-item-${name}`}>
                    <input
                      id={`dsp-item-${name}`}
                      type="checkbox"
                      value={name}
                      checked={isChecked}
                      onChange={handleChange}
                    />
                    <CheckboxLabel
                      htmlFor={`dsp-item-${name}`}
                      dspLimitReached={dspLimitReached}
                      minDSPLimitReached={selectedDSPs.length === 0}
                      checked={isChecked}
                      isDropdownDesign={isDropdownDesign}
                    >
                      <Checkbox
                        enabledText="Disable"
                        disabledText="Enable"
                        isChecked={isChecked}
                        insideDropdown
                      />
                      <span>{item.displayName}</span>
                    </CheckboxLabel>
                  </ListItem>
                );
              })}
            </ul>
          ) : (
            this.state.searchValue.length > 0 && (
              <HelperText>
                {`We could not find a ${
                  selectedDSPsIds
                    ? 'Source of Stream'
                    : `DSP${
                        !!savedCustomDSPLists?.length && ' or a Custom DSP List'
                      }`
                } that matches your search query.`}
              </HelperText>
            )
          )}
        </Content>
      </Container>
    );
  }
}

DSPList.propTypes = {
  /** All the DSPs for this topic coming from the utlity call. */
  allDSPs: arrayOf(
    shape({
      id: oneOf(number | string),
      name: string.isRequired,
      displayName: string.isRequired,
      color: string,
    })
  ).isRequired,
  /** List of the selected DSPs */
  selectedDSPs: any,
  /** Method to toggle the active state of a DSP */
  toggleDSP: func.isRequired,
  /** Have we reached the maximum limit of items that we can select? */
  dspLimitReached: bool,
  isDropdownDesign: bool,
  label: string,
  savedCustomDSPLists: any,
  selectedCustomDSPList: any,
  onSelectCustomDSPList: func,
  switchToEditView: func,
};

export default DSPList;
